import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  CardContent,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Container,
  Grid,
  Chip,
  Switch,
} from "@mui/material";
import ProductPrice from "./price_detail";
import PlanHistoryModal from "./history_detail";
import productApi from "../../../services/product";
import productPricingApi from "../../../services/product_pricing";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import CommunicationPriceList from '../pricing/communicationpricelist';

const ProductPricing = () => {
  const dispatch = useDispatch();
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems
    ?.find((item) => item.name.toLowerCase() === "products")
    ?.submenus?.find((item) => item.name.toLowerCase() === "pricing");
  const isDisabled = createMenuItem?.access_type === "read_write";
  const [selectedProduct, setSelectedProduct] = useState("");
  const [showProductPrice, setShowProductPrice] = useState(false);
  const [productList, setProductList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProductUUID, setSelectedProductUUID] = useState("");
  const [planDetails, setPlanDetails] = useState([]);
  const [manageMode, setManageMode] = useState("create");
  const [priceData, setPriceData] = useState();
  const [initialPricingData, setInitialPricingData] = useState({
    prices: {},
    discounts: {},
    offerPrices: {},
    offerPercentages: {},
    productPlansPricingDetails: [],
    offerApplicable: false,
    offerValidFrom: null,
    offerValidTo: null,
    offerText: "",
  });
  const [planName, setPlanName] = useState([]);
  const [isNew, setIsNew] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const productSelect = useCallback(() => {
    dispatch(showLoader());

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setProductList(res?.data?.data);
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error("Error response:", err);
    };

    productApi.getAllProduct().then(onSuccess).catch(onFailure);
  }, [dispatch]);

  useEffect(() => {
    productSelect();
  }, [productSelect]);

  
 
  const handleProductChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedProduct(selectedValue);

    if (selectedValue === "Communication Channel") {
      setShowProductPrice(false); 
      setSelectedProductUUID(""); 
      return; 
    }

    const selectedProductItem = productList.find(
      (product) => product.name === selectedValue
    );

    if (!selectedProductItem) {
      console.error("Selected product not found in productList");
      return; 
    }

    if (manageMode === "create") {
      setSelectedProductUUID(selectedProductItem.uuid);
      setShowProductPrice(true);
    }
    if (manageMode === "ongoing") {
      onGoingData(selectedProductItem.uuid);
    }
  };

  const planData = useCallback(
    (selectedProductUUID) => {
      const uuid = selectedProductUUID;
      dispatch(showLoader());

      setPlanDetails([]);
      const onSuccess = (res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          const data = res?.data?.data;
          if (data.length > 0) {
            setPlanDetails(data);
            setShowProductPrice(true);
          } else {
            setShowProductPrice(false);
          }
          setPriceData();
          setInitialPricingData({
            prices: {},
            discounts: {},
            offerPrices: {},
            offerPercentages: {},
            productPlansPricingDetails: [],
          });
          setIsNew();
          setManageMode("create");
        }
      };

      const onFailure = (err) => {
        dispatch(hideLoader());
        console.error("Error response:", err?.response?.msg);
        setShowProductPrice(false);
        if (err?.response?.status === false) {
          setPlanDetails([]);
        }
      };

      productPricingApi
        .getPlanAgainstProduct(uuid)
        .then(onSuccess)
        .catch(onFailure);
    },
    [dispatch]
  );

  useEffect(() => {
    if (selectedProductUUID) {
      planData(selectedProductUUID);
    }
  }, [selectedProductUUID, planData]);

  const resetFields = () => {
    setSelectedProduct("");
    setShowProductPrice(false);
    setProductList([]);
    setSelectedProductUUID("");
    setPlanDetails([]);
    setManageMode("create");
    setPriceData();
    setInitialPricingData({
      prices: {},
      discounts: {},
      offerPrices: {},
      offerPercentages: {},
      productPlansPricingDetails: [],
    });
    setPlanName([]);
    setShowProductPrice(false);
    productSelect();
    setPaginationModel({ page: 0, pageSize: 10 });
    setIsNew();
  };

  const onGoingData = useCallback(
    (selectedProductUUID) => {
      dispatch(showLoader());

      const uuid = selectedProductUUID;
      const onSuccess = (res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          const data = res?.data?.data;
          setPriceData(data);
          setPlanName([]);
        }
      };

      const onFailure = (err) => {
        dispatch(hideLoader());
        console.error("Error response:", err?.response?.msg);
        setShowProductPrice(false);
      };

      productPricingApi
        .getOngoingPricing(uuid)
        .then(onSuccess)
        .catch(onFailure);
    },
    [dispatch]
  );
  const handleToggleChange = () => {
    setIsNew((prev) => {
      const newValue = !prev;
      if (newValue) {
        onGoingData(selectedProductUUID);
        setManageMode("ongoing");
      } else {
        planData(selectedProductUUID);
        setManageMode("create");
      }
      return newValue;
    });
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: "4" }}>
        <Typography variant="h6" gutterBottom>
          Product Pricing
        </Typography>
        <Box sx={{ margin: "auto", marginTop: "10px", maxWidth: "100%" }}>
          <Card sx={{ marginBottom: 2, width: "100%" }} elevation={3}>
            <CardContent>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 2,
                }}
              >
                {manageMode === "edit" && (
                  <Chip
                    label="Create"
                    clickable
                    onClick={() => {
                      setManageMode("edit");
                      resetFields();
                    }}
                    color="primary"
                    variant="filled"
                    size="medium"
                    sx={{ marginRight: "5px" }}
                  />
                )}
                <Chip
                  label="Price History"
                  clickable
                  onClick={handleModalOpen}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ marginRight: "5px" }}
                />
              </Grid>
              {isDisabled && (
                <Grid container spacing={2} alignItems="center">
                  {manageMode !== "edit" && (
                    <Grid item md={4} sm={12}>
                      <Select
                        value={selectedProduct}
                        onChange={handleProductChange}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="" disabled>
                          Select a product
                        </MenuItem>
                        {productList.map((product) => (
                          <MenuItem key={product.uuid} value={product.name}>
                            {product.name}
                          </MenuItem>
                        ))}
                        <MenuItem value="Communication Channel">
                          Communication Channel
                        </MenuItem>
                      </Select>
                    </Grid>
                  )}
                  {selectedProduct && (
                    <Grid item md={2} sm={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={resetFields}
                      >
                        Reset
                      </Button>
                    </Grid>
                  )}
                  {selectedProduct &&
                    (manageMode === "create" || manageMode === "ongoing") && (
                      <Grid
                        item
                        md={4}
                        sm={12}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography variant="body2" sx={{ marginRight: 1 }}>
                          New
                        </Typography>
                        <Switch
                          checked={isNew}
                          onChange={handleToggleChange}
                          color="primary"
                          inputProps={{
                            "aria-label": "Toggle between New and Ongoing",
                          }}
                        />
                        <Typography variant="body2" sx={{ marginLeft: 1 }}>
                          Ongoing
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
      {selectedProduct === "Communication Channel" ? (
        <CommunicationPriceList />
      ) : selectedProduct || priceData ? (
        <ProductPrice
          planDetails={planDetails}
          productUUID={selectedProductUUID}
          setManageMode={setManageMode}
          manageMode={manageMode}
          priceData={priceData}
          setPlanDetails={setPlanDetails}
          setInitialPricingData={setInitialPricingData}
          initialPricingData={initialPricingData}
          setPlanName={setPlanName}
          planName={planName}
          resetFields={resetFields}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
        />
      ) : (
        <Container maxWidth="xl" sx={{ mt: "4" }} align="center">
          <Typography variant="subtitle1">No Plans Available</Typography>
        </Container>
      )}

      <PlanHistoryModal
        open={openModal}
        onClose={handleModalClose}
        setManageMode={setManageMode}
        manageMode={manageMode}
        setPriceData={setPriceData}
        priceData={priceData}
        setPlanDetails={setPlanDetails}
        setInitialPricingData={setInitialPricingData}
        initialPricingData={initialPricingData}
        setPlanName={setPlanName}
        planName={planName}
        productSelect={productSelect}
        showProductPrice={showProductPrice}
        setShowProductPrice={setShowProductPrice}
        setPaginationModel={setPaginationModel}
        paginationModel={paginationModel}
        resetFields={resetFields}
        planDetails={planDetails}
      />
    </>
  );
};

export default ProductPricing;
